import React, { useState, useEffect } from 'react'
import * as api from '../../services/api.js'
import Modal from '../../components/Modal.jsx'
import PageEditor from './PageEditor.jsx'
import TemplatesPicker from './TemplatesPicker.jsx'

function formatDate(iso) {
  if (!iso) return ''
  const d = new Date(iso + (iso.length === 10 ? 'T12:00:00' : ''))
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
}

function parseContent(raw) {
  if (typeof raw === 'object' && raw !== null) return raw
  try { return JSON.parse(raw || '{}') } catch { return {} }
}

function firstTextPreview(blocks) {
  for (const b of (blocks || [])) {
    if (b.block_type === 'text' || b.block_type === 'heading1' || b.block_type === 'heading2') {
      const c = parseContent(b.content)
      if (c.text?.trim()) return c.text.trim().slice(0, 80)
    }
    if (b.block_type === 'checklist') {
      const c = parseContent(b.content)
      const first = c.items?.find(i => i.text)
      if (first) return first.text.slice(0, 80)
    }
  }
  return ''
}

// ── Page card ─────────────────────────────────────────────────────────────────

function PageCard({ page, preview, onOpen, onPin, onDelete }) {
  return (
    <div className="ms-page-card" onClick={() => onOpen(page)}>
      <div
        className="cover-strip"
        style={page.cover_image
          ? { backgroundImage: `url(${page.cover_image})`, backgroundSize: 'cover', backgroundPosition: 'center' }
          : { background: page.cover_color || '#f5f0e8' }
        }
      />
      <div className="pc-body">
        <div className="pc-icon-title">
          <span className="pc-icon">{page.icon || '📄'}</span>
          <span className="pc-title">{page.title || 'Untitled'}</span>
          {page.pinned ? <span style={{ fontSize: '0.7rem', marginLeft: 'auto' }}>📌</span> : null}
        </div>
        {preview && <div className="pc-preview">{preview}</div>}
        <div className="pc-footer">
          <span>{formatDate(page.updated_at)}</span>
        </div>
      </div>
      <div className="ms-page-card-actions" onClick={e => e.stopPropagation()}>
        <button
          className={`ms-card-icon-btn${page.pinned ? ' pinned' : ''}`}
          title={page.pinned ? 'Unpin' : 'Pin'}
          onClick={() => onPin(page)}
        >
          📌
        </button>
        <button
          className="ms-card-icon-btn"
          title="Delete page"
          onClick={() => onDelete(page)}
        >
          ✕
        </button>
      </div>
    </div>
  )
}

// ── MyPages main ──────────────────────────────────────────────────────────────

export default function MyPages({ layoutClass, initialOpenPageId }) {
  const [pages, setPages]               = useState([])
  const [previews, setPreviews]         = useState({})
  const [openPage, setOpenPage]         = useState(null)
  const [search, setSearch]             = useState('')
  const [showTemplates, setShowTemplates] = useState(false)
  const [loading, setLoading]           = useState(true)
  const [confirmDelete, setConfirmDelete] = useState(null)

  useEffect(() => {
    api.getMyPages().then(rows => {
      const ps = rows || []
      setPages(ps)
      setLoading(false)
      // Load previews: first block of each page
      ps.forEach(p => {
        api.getPageBlocks(p.id).then(blocks => {
          setPreviews(prev => ({ ...prev, [p.id]: firstTextPreview(blocks) }))
        })
      })
      // Auto-open page from Overview pinned card
      if (initialOpenPageId) {
        const target = ps.find(p => p.id === initialOpenPageId)
        if (target) setOpenPage(target)
      }
    })
  }, [initialOpenPageId])

  async function handleCreate() {
    const res = await api.createMyPage({
      title: 'Untitled',
      icon: '📄',
      cover_color: '#f5f0e8',
      layout: 'cosy',
      pinned: 0,
    })
    if (res?.id) {
      const newPage = { id: res.id, title: 'Untitled', icon: '📄', cover_color: '#f5f0e8', layout: 'cosy', pinned: 0, updated_at: new Date().toISOString() }
      setPages(prev => [newPage, ...prev])
      setOpenPage(newPage)
    }
  }

  function handlePageCreatedFromTemplate(pageId) {
    setShowTemplates(false)
    api.getMyPages().then(rows => {
      const ps = rows || []
      setPages(ps)
      const target = ps.find(p => p.id === pageId)
      if (target) setOpenPage(target)
    })
  }

  async function handlePin(page) {
    const next = !page.pinned
    await api.updateMyPage(page.id, { pinned: next })
    setPages(prev => {
      const updated = prev.map(p => p.id === page.id ? { ...p, pinned: next } : p)
      return [...updated.filter(p => p.pinned), ...updated.filter(p => !p.pinned)]
    })
  }

  function requestDelete(page) {
    setConfirmDelete(page)
  }

  async function confirmDeletePage() {
    if (!confirmDelete) return
    await api.deleteMyPage(confirmDelete.id)
    setPages(prev => prev.filter(p => p.id !== confirmDelete.id))
    setConfirmDelete(null)
  }

  function handlePageUpdated(updated) {
    setPages(prev => prev.map(p => p.id === updated.id ? { ...p, ...updated } : p))
  }

  const filtered = pages.filter(p =>
    !search || (p.title || '').toLowerCase().includes(search.toLowerCase())
  )

  // ── Editor view ──
  if (openPage) {
    return (
      <PageEditor
        page={openPage}
        onBack={() => { setOpenPage(null); api.getMyPages().then(rows => setPages(rows || [])) }}
        onPageUpdated={handlePageUpdated}
      />
    )
  }

  // ── Template picker ──
  if (showTemplates) {
    return (
      <div>
        <button className="page-editor-back" onClick={() => setShowTemplates(false)}>
          ← Back
        </button>
        <h2 style={{ fontFamily: 'var(--font-serif)', fontWeight: 400, marginBottom: '1rem', color: 'var(--ink)', fontSize: '1.2rem' }}>
          Choose a template
        </h2>
        <TemplatesPicker
          onClose={() => setShowTemplates(false)}
          onCreated={handlePageCreatedFromTemplate}
        />
      </div>
    )
  }

  return (
    <div>
      {/* Controls */}
      <div className="ms-controls-row">
        <button className="save-btn ready" style={{ fontSize: '0.82rem', padding: '7px 16px' }} onClick={handleCreate}>
          + New page
        </button>
        <button className="ghost-btn" style={{ fontSize: '0.82rem' }} onClick={() => setShowTemplates(true)}>
          From template
        </button>
        <input
          className="ms-search-input"
          placeholder="Search pages…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </div>

      {/* Empty state */}
      {!loading && pages.length === 0 && (
        <div className="ms-empty">
          <div className="ms-empty-icon">✦</div>
          <div className="ms-empty-title">Your pages are waiting</div>
          <div className="ms-empty-desc">Create a page to capture thoughts, notes, recipes, plans — anything at all.</div>
          <div className="ms-empty-actions">
            <button className="save-btn ready" onClick={handleCreate}>Create your first page</button>
            <button className="ghost-btn" onClick={() => setShowTemplates(true)}>Browse templates</button>
          </div>
        </div>
      )}

      {/* No results */}
      {!loading && pages.length > 0 && filtered.length === 0 && (
        <p style={{ color: 'var(--stone)', fontStyle: 'italic', fontSize: '0.88rem' }}>
          No pages match "{search}"
        </p>
      )}

      {/* Page grid */}
      {filtered.length > 0 && (
        <div className="ms-pages-grid">
          {filtered.map(page => (
            <PageCard
              key={page.id}
              page={page}
              preview={previews[page.id] || ''}
              onOpen={setOpenPage}
              onPin={handlePin}
              onDelete={requestDelete}
            />
          ))}
        </div>
      )}

      {/* Delete confirmation */}
      {confirmDelete && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
          zIndex: 500, display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: '1rem',
        }}>
          <div style={{
            background: 'var(--card-bg, var(--surf))',
            borderRadius: '14px',
            padding: '1.5rem',
            maxWidth: '360px',
            width: '100%',
          }}>
            <p style={{ marginBottom: '1rem', color: 'var(--ink)', fontSize: '0.95rem' }}>
              Delete <strong>"{confirmDelete.title}"</strong>? This cannot be undone.
            </p>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="ghost-btn" onClick={() => setConfirmDelete(null)}>Cancel</button>
              <button
                className="save-btn ready"
                style={{ background: 'var(--bark)' }}
                onClick={confirmDeletePage}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
